/* product-recommendations.js - the loader behind sections/related-products.liquid
   and blocks/product-complementary.liquid.

   Recommendations exist only through the Product Recommendations API, so the
   page ships an EMPTY SHELL carrying the data URL and this file fetches the
   real markup, swaps it in and starts the carousel. Nothing renders server
   side, which is the API and not a shortcut.

   IT FETCHES LATE, ON PURPOSE. The strip lives below the fold on every route
   that carries it, so the request is armed by an IntersectionObserver with a
   generous rootMargin: it lands before the shopper reaches the strip and never
   competes with the product image for bandwidth.

   TEARDOWN IS KEYED BY SECTION ID, never by node. The theme editor re-renders a
   section by replacing its innerHTML and fires shopify:section:load with NO
   matching unload, so by the time we hear about it the element we armed is
   already detached and unreachable. The id is the only stable handle, and a
   sweep drops any entry whose node has left the document.

   init() is idempotent and re-runs on shopify:section:load, which the editor
   needs and never triggers by itself: a module that only ran on
   DOMContentLoaded would leave an observer watching a detached node after every
   merchant edit.

   A FAILED FETCH TELLS THE TRUTH. There is no spinner to leave spinning and no
   empty state to leave lying: the shell is hidden, the strip is simply not
   there, and no exception ever reaches the page. */
(function () {
  'use strict';

  var flag = document.documentElement;
  if (flag.hasAttribute('data-pp-recommendations')) return;
  flag.setAttribute('data-pp-recommendations', '');

  var SELECTOR = '[data-recommendations]';
  var watchers = Object.create(null);

  function release(id) {
    var watcher = watchers[id];
    if (!watcher) return;
    watcher.observer.disconnect();
    delete watchers[id];
  }

  function sweep() {
    Object.keys(watchers).forEach(function (id) {
      if (!watchers[id].node.isConnected) release(id);
    });
  }

  /* TWO CUSTOM ELEMENTS ARRIVE IN THE FETCHED MARKUP AND NEITHER CAN DEFINE
     ITSELF. <pp-slider> and <pp-card-gallery> both upgrade on their own once
     their element is defined, and innerHTML upgrades the instances already
     sitting in the page the moment that happens. What innerHTML will NEVER do
     is execute a <script src>, and a <script src> is exactly how both modules
     ship: the slider's from the section shell, the gallery's emitted per card
     by snippets/card-product.liquid. So each URL travels as DATA on the shell
     and the loader in assets/theme.js appends a real <script> to the head,
     which does execute.

     THE GALLERY ARM IS NOT SYMMETRY, IT IS THE ONLY PATH IT HAS. A card is
     drawn server side on the collection, search and home routes, never on the
     routes that carry these rails: a product page and a cart hold no card at
     all until this fetch lands one. So card-gallery.js has never been
     requested, pp-card-gallery is an unknown tag, and the galleries that just
     arrived keep their data-pp-card-idle attribute: no arrows, no tab stop,
     dead. Measured before the fix: 4 inert galleries on the PDP, 1 on the cart,
     0 arrows.

     ONE FETCH FOR ALL OF THEM. assets/theme.js dedupes loadModule by src, so
     the two rails and blocks/product-complementary.liquid asking for the same
     file share a single resolved promise. */
  function armSlider(node) {
    var PP = window.PP;
    var src = node.getAttribute('data-slider-js');
    if (!src || !node.querySelector('pp-slider')) return;
    if (!PP || typeof PP.loadModule !== 'function') return;
    PP.loadModule(src).then(null, function () {});
  }

  function armGallery(node) {
    var PP = window.PP;
    var src = node.getAttribute('data-gallery-js');
    if (!src || !node.querySelector('pp-card-gallery')) return;
    if (!PP || typeof PP.loadModule !== 'function') return;
    PP.loadModule(src).then(null, function () {});
  }

  function settle(node, id) {
    node.removeAttribute('data-loading');
    node.setAttribute('data-loaded', '');
    release(id);
  }

  function load(node, id) {
    if (node.hasAttribute('data-loading') || node.hasAttribute('data-loaded')) return;
    var url = node.getAttribute('data-url');
    if (!url) return;
    node.setAttribute('data-loading', '');

    fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      })
      .then(function (markup) {
        /* The API re-renders a whole SECTION, so a host holding more than one
           shell (blocks/product-complementary.liquid lives inside main-product)
           gets every one of them back in the response. Match our own
           data-section-id first, or two shells would both swap in the first
           one's products. The fallback is what sections/related-products.liquid
           relies on: its shell asks with the section id it also publishes, so
           the exact match normally hits there too, and first-match stays the
           floor if a host ever publishes an id it did not request with.

           The id is platform data, so the selector it builds escapes " and \
           and still runs behind a try/catch: an id nobody anticipated may cost
           us the exact match and drop us to first-match, never the swap. */
        var doc = new DOMParser().parseFromString(markup, 'text/html');
        var fresh = null;
        try {
          fresh = doc.querySelector(SELECTOR + '[data-section-id="' + id.replace(/["\\]/g, '\\$&') + '"]');
        } catch (error) {
          fresh = null;
        }
        if (!fresh) fresh = doc.querySelector(SELECTOR);
        settle(node, id);
        /* An empty body is the honest answer for a product Shopify has no
           recommendations for yet. Hiding the shell removes its padding too, so
           the page never shows a blank band where a strip was hoped for. */
        if (!fresh || fresh.innerHTML.trim() === '') {
          node.hidden = true;
          return;
        }
        node.innerHTML = fresh.innerHTML;
        armSlider(node);
        armGallery(node);
      })
      .catch(function () {
        settle(node, id);
        node.hidden = true;
      });
  }

  function arm(node) {
    var id = node.getAttribute('data-section-id');
    if (!id) return;
    /* Same id, new node: the editor just rebuilt this section, so the observer
       still watching the old node has to go before a new one is registered. */
    release(id);
    if (node.hasAttribute('data-loaded') || !node.getAttribute('data-url')) return;
    if (typeof IntersectionObserver !== 'function') {
      load(node, id);
      return;
    }
    var observer = new IntersectionObserver(
      function (entries) {
        if (!entries[entries.length - 1].isIntersecting) return;
        load(node, id);
      },
      { rootMargin: '0px 0px 600px 0px' }
    );
    watchers[id] = { node: node, observer: observer };
    observer.observe(node);
  }

  function init(scope) {
    sweep();
    var nodes = (scope || document).querySelectorAll(SELECTOR);
    Array.prototype.forEach.call(nodes, function (node) {
      arm(node);
    });
  }

  init();

  document.addEventListener('shopify:section:load', function (event) {
    var scope = event.target && event.target.querySelectorAll ? event.target : document;
    init(scope);
  });

  document.addEventListener('shopify:section:unload', function () {
    sweep();
  });
})();
